import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox, Input } from '@/components/ui'
import { Plus, Trash, User2Icon } from 'lucide-react'
import { Languages, Stage, Stages } from '../../common/models'
import { IColumn, Table } from '../../core/components/Table'
import { sanitizeDate } from '../../core/constants/times'
import { useTableParams } from '../../core/hooks/table-params'
import { useCheckPermission } from '../../core/hooks/use-check-permission'
import { getSettingsLanguages } from '../../core/services/festival-settings'
import {
  deleteStages,
  getAllStages,
  getStages,
  publishStages,
  unpublishStages,
} from '../../core/services/stage'
import { useAppSelector } from '../../store'
import { ColorCircle } from './ColorCircle'
import { CreateStageModal } from './CreateStageModal'
import { DeleteConfirmationModal } from './DeleteConfirmationModal'
import { MessageBox } from './messages/MessageBox'
import { Icon } from '@/common/components/Icon'
import { ActionsMenu } from '@/components/ActionsMenu'
import Loader from '@/common/components/Loader'
import { find } from 'lodash'

export const StagesPage: React.FC = () => {
  const { size, sortBy, setSortBy } = useTableParams({ defaultSize: 100 })
  const navigate = useNavigate()
  const { festivalName, editionId } = useParams()
  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const festivalFullName = storeFestivals.find(
    (f) => f.platformName === festivalName,
  )?.name
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false)
  const [allLanguages, setAllLanguages] = useState<Languages[]>([])
  const [currentStage, setCurrentStage] = useState<Stage>()
  const [stages, setStages] = useState<Stages[]>([])
  const [isCheckAll, setIsCheckAll] = useState(false)
  const [isCheck, setIsCheck] = useState<string[]>([])
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)
  const [isEditMode, setIsEditMode] = useState<boolean>(false)
  const [sortDirection, setSortDirection] = useState<
    'ASC' | 'DESC' | undefined
  >(undefined)
  const [loading, setLoading] = useState<boolean>(false)

  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const { hasEditPermission } = useCheckPermission({ page: 'STAGES' })

  const getData = useCallback(async () => {
    setLoading(true)
    try {
      const stageResponse = await getAllStages({ sortBy, size, sortDirection })
      setStages(stageResponse.data.content)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [sortBy, size, editionId, festivalName, sortDirection])

  useEffect(() => {
    getData()
  }, [getData])

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        setLoading(true)
        const allLanguagesResponse = await getSettingsLanguages()
        if (allLanguagesResponse.status === 200) {
          setAllLanguages(allLanguagesResponse.data)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }, [editionId, festivalName])

  const handleDelete = () => {
    if (!hasEditPermission) {
      return
    }
    if (isCheck.length > 0) {
      setDeleteConfirmationModal(true)
    }
  }

  const deleteFunc = async () => {
    if (!hasEditPermission) {
      return
    }
    setLoading(true)
    try {
      const { status } = await deleteStages(isCheck)
      getData()
      setIsCheck([])
      if (status === 204) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Delete Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const handlePublish = async (isCheck: string[]) => {
    if (!hasEditPermission) {
      return
    }
    setLoading(true)
    try {
      const { status } = await publishStages(isCheck)
      getData()
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Publish Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleUnpublish = async (isCheck: string[]) => {
    if (!hasEditPermission) {
      return
    }
    setLoading(true)
    try {
      const { status } = await unpublishStages(isCheck)
      getData()
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Unpublish Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSelectAll = () => {
    if (!hasEditPermission) {
      return
    }
    setIsCheckAll(!isCheckAll)
    setIsCheck(stages.map((stg) => stg.id))
    if (isCheckAll) {
      setIsCheck([])
    }
  }

  const handleClick = (id: string, checked: boolean) => {
    if (!hasEditPermission) {
      return
    }
    setIsCheck([...isCheck, id])
    if (!checked) {
      setIsCheck(isCheck.filter((item) => item !== id))
    }
  }

  const handleSortBy = (sort: string) => {
    if (sort === sortBy) {
      if (sortDirection === 'ASC') {
        setSortDirection('DESC')
      } else {
        setSortDirection(undefined)
        setSortBy(undefined)
      }
    } else {
      setSortDirection('ASC')
      setSortBy(sort)
    }
  }

  const columns: IColumn[] = [
    {
      title: (
        <Checkbox
          onCheckedChange={handleSelectAll}
          onChange={handleSelectAll}
          disabled={!hasEditPermission}
        />
      ),
      accessor: '',
      render: (row) => (
        <Checkbox
          onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
          checked={isCheck.includes(row.id)}
          disabled={!hasEditPermission}
        />
      ),

      width: '16px',
    },
    {
      title: 'Name',
      accessor: 'name',
      render: (row) => (
        <span className="min-w-[200px] line-clamp-1">{row.name}</span>
      ),
    },
    {
      title: 'Image',
      accessor: 'image',
      render: (row) => {
        return (
          <div className="w-[40px] h-[40px] rounded-[10px] flex justify-center items-center bg-white p-1 text-black">
            {row.imageLink ? (
              <img
                src={row.imageLink}
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="w-[40px] h-[40px] rounded-[10px] flex justify-center items-center font-size-20">
                <User2Icon className="w-4 h-4" />
              </div>
            )}
          </div>
        )
      },
    },
    {
      title: 'Color',
      accessor: '',
      render: (row) => {
        return (
          <ColorCircle gradient={row?.gradient} text={row?.gradient?.name} />
        )
      },
    },
    {
      title: 'Ordinal',
      accessor: 'ordinal',
      sortable: true,
      render: (row: Stage) => (
        <Input
          value={row.ordinal}
          className="bg-monochrome-4 border-none min-w-[100px]"
          readOnly
        />
      ),
    },

    {
      title: 'Published',
      accessor: 'published',
      sortable: true,
      render: (row) => (
        <Checkbox
          onCheckedChange={() => {
            if (row.published) {
              handleUnpublish([row.id])
            } else {
              handlePublish([row.id])
            }
          }}
          checked={row.published}
          disabled={!hasEditPermission}
        />
      ),
    },
    {
      title: 'Publish Date',
      accessor: 'publishedAt',
      sortable: true,
      render: (row) => <>{sanitizeDate(row.publishedAt)}</>,
    },
    {
      title: 'Details',
      accessor: '',
      render: (row) => (
        <Link
          to={`/stages/${festivalName}/${editionId}/${row.id}`}
          className="view pt-2 text-accent-1 underline"
        >
          View
        </Link>
      ),
    },
    {
      title: 'Edit',
      accessor: '',
      render: (row) => (
        <a
          className="view pt-2 text-accent-1 underline"
          href="#"
          onClick={async (e) => {
            e.preventDefault()
            if (!hasEditPermission) {
              return
            }
            setShowCreateModal(true)
            setIsEditMode(true)
            const response = await getStages(row.id)
            setCurrentStage(response.data)
          }}
        >
          Edit
        </a>
      ),
    },
  ]

  return (
    <>
      {loading && <Loader />}
      <Header
        pageTitle="Stages"
        actions={
          <>
            {hasEditPermission && isCheck.length >= 1 && (
              <>
                <ActionsMenu
                  actions={[
                    { label: 'Publish', onClick: () => handlePublish(isCheck) },
                    {
                      label: 'Unpublish',
                      onClick: () => handleUnpublish(isCheck),
                    },
                  ]}
                />
                <Button variant="destructive" onClick={handleDelete}>
                  Delete
                  <Icon
                    icon="close-circle"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                  />
                </Button>
              </>
            )}
            {hasEditPermission && (
              <Button
                variant="primary-bordered"
                onClick={() => {
                  setIsEditMode(false)
                  setShowCreateModal(true)
                  setCurrentStage(undefined)
                }}
              >
                Add New
                <Icon
                  icon="plus-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                />
              </Button>
            )}
          </>
        }
      />
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle title="Stages" icon="stages-bold" className="mb-5" />
        <Table
          handleSortBy={handleSortBy}
          isCheck={isCheck}
          columns={columns}
          data={stages}
          rowReorder
          onRowReorder={(next) => setStages(next)}
          maxHeight="calc(100vh - 220px)"
          alignment="middle"
          className="[&_th]:!whitespace-nowrap"
        />
      </Card>
      <CreateStageModal
        stage={currentStage}
        setShowEditModal={setShowCreateModal}
        showEditModal={showCreateModal}
        setStages={setStages}
        size={size}
        sortBy={sortBy}
        isEditMode={isEditMode}
        setIsEditMode={setIsEditMode}
        setLoading={setLoading}
        allLanguages={allLanguages}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={isCheck.length > 1 ? 'Stages' : 'Stage'}
        deleteFunc={deleteFunc}
        deletedItem={
          isCheck.length > 1
            ? `${isCheck.length} stages`
            : find(stages, { id: isCheck[0] })?.name
        }
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
